/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable camelcase */
import React, { useContext, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { MLTooltipComponent } from 'ml-components-platform-react';
import {
    DataTable,
    DisplayColumn,
} from '@jutro/legacy/datatable';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

import messages from './MLSelectPaymentMethodComponent.messages.js';

import metadata from './MLSelectPaymentMethodComponent.metadata.json5';
import styles from './MLSelectPaymentMethodComponent.module.scss';
import { Link } from '@jutro/router';

// MLSelectPaymentMethod.js directive
function MLSelectPaymentMethodComponent(props) {
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);

    const {
        paymentInstruments,
        paymentInstrumentsForAllPolicies,
        paymentRequests,
        selectedPaymentInstrument,
        onPaymentInstrumentChange,
        onInvalidCreditCard,
        paymentMethod,
        editPaymentInstrument,
        deletePaymentInstrument,
        hasPaymentError,
        resetPaymentError,
        addPaymentInstrument,
        isGpa,
        showRestrictedPaymentMethodError,
        showRestrictedPaymentMethodWarning
    } = props;

    const {
        MLAccountPaymentMethods
    } = appConfig;

    const isValidPaymentInstrument = useCallback((paymentInstrument, paymentMethodVlue) => {
        return paymentInstrument.paymentMethod === paymentMethodVlue && (!!paymentInstrument.portalAvailable || paymentInstrument.paymentMethod === MLAccountPaymentMethods.CREDIT_CARD);
    }, [MLAccountPaymentMethods.CREDIT_CARD]);

    const checkCreditCardExpirationDate = useCallback((paymentInstrument) => {
        if (paymentInstrument) {
            const paymentVM = viewModelService.create(paymentInstrument, 'pc', 'metlife.edge.capabilities.policycommon.paymentdetails.dto.PaymentInstrumentDTOML');
            if (!paymentVM.creditCardExpDate.aspects.valid) {
                onInvalidCreditCard();
            }
        }
    }, [onInvalidCreditCard, viewModelService]);

    const onSelectPaymentInstrument = useCallback((accountToken) => {
        resetPaymentError();
        paymentRequests.value.forEach((item) => {
            item.selectedPaymentInstrument_ML = accountToken;
        });
        const paymentInstrument = paymentInstruments.find((instrument) => instrument.token === accountToken);
        checkCreditCardExpirationDate(paymentInstrument);
        onPaymentInstrumentChange(paymentInstrument);
    }, [checkCreditCardExpirationDate, onPaymentInstrumentChange, paymentInstruments, paymentRequests.value, resetPaymentError]);

    const numberOfPaymentInstrumentsForMethod = useCallback((method) => {
        let numPaymentInstruments = 0;
        paymentInstruments.forEach((paymentInstrument) => {
            if (isValidPaymentInstrument(paymentInstrument, method)) {
                numPaymentInstruments++;
            }
        });
        return numPaymentInstruments;
    }, [isValidPaymentInstrument, paymentInstruments]);

    const showDeletePaymentInstrument = useCallback((paymentInstrument) => {
        return numberOfPaymentInstrumentsForMethod(paymentInstrument.paymentMethod) > 1 && paymentInstrumentsForAllPolicies.findIndex((currentInstrument) => currentInstrument === paymentInstrument.token) === -1;
    }, [numberOfPaymentInstrumentsForMethod, paymentInstrumentsForAllPolicies]);

    const showPaymentInstrumentError = (paymentInstrument) => {
        return hasPaymentError() && paymentInstrument === selectedPaymentInstrument.value;
    };

    const getAccountName = (paymentInstrument) => {
        return <FormattedMessage
            {...messages.xEndingInx}
            values={{
                nickname: <strong>{paymentInstrument.nickname ? paymentInstrument.nickname : paymentInstrument.accountType_ML}</strong>,
                last4Digits: paymentInstrument.last4Digits
            }}
        />
    };

    const getTitleContent = () => {
        return (
            <>
                {isGpa && <><b>{translator(messages.titleText)}</b><MLTooltipComponent tooltipHeader={translator(messages.tooltipHeader)} tooltipContent={translator(messages.tooltipContent)} tooltipWrapperClassName={styles.tooltipWraper} /></>}
                {!isGpa && <span><p>{translator(messages.titleText)}</p></span>}
            </>
        );
    };

    const generateActionColumn = useCallback((row, index) => {
        return (
            <div className='text-align-right'>
                <span>
                    <Link id={`edit${index}`} onClick={() => editPaymentInstrument(row.token)}>{translator(messages.edit)}</Link>
                </span>
                <br />
                {showDeletePaymentInstrument(row) && (
                    <span>
                        <Link id={`remove${index}`} onClick={() => deletePaymentInstrument(row)}>{translator(messages.delete)}</Link>
                    </span>
                )}
            </div>
        );
    }, [deletePaymentInstrument, editPaymentInstrument, showDeletePaymentInstrument, translator]);

    const data = _.filter(
        _.orderBy(paymentInstruments, ['last4Digits']),
        (paymentInstrument) => isValidPaymentInstrument(paymentInstrument, paymentMethod.value))

    const selectedRows = useMemo(() => [data.findIndex((pms) => pms.token === selectedPaymentInstrument.value?.token)], [data, selectedPaymentInstrument.value?.token])

    const handleSelection = useCallback((rows) => {
        const rowIndex = rows[0];
        const selectedToken = data[rowIndex].token;
        onSelectPaymentInstrument(selectedToken);
    }, [data, onSelectPaymentInstrument]);

    const generateCellContent = useCallback((paymentInstrument) => <div>{getAccountName(paymentInstrument)}</div>, []);

    const shouldHidePaymentInstrumentsTable = (instruments, method) => {
        return !(instruments && instruments.some((instrument) => instrument.paymentMethod === method.value && instrument.portalAvailable === true));
    };

    const getNoPaymentInstrumentText = (method) => {
        return method && method.value === 'creditcard' ? translator(messages.creditCardNotListedOnFile) : translator(messages.bankAccountNotListedOnFile)
    };

    const shouldShowNoPaymentInstrumentText = (instruments, method) => {
        return method && method.value !== 'agencysweep' && shouldHidePaymentInstrumentsTable(instruments, method);
    };

    // eslint-disable-next-line max-len
    const selectPaymentMethodTable = useCallback(() => <div id="instrumentTableWrapper" className={shouldHidePaymentInstrumentsTable(paymentInstruments, paymentMethod) ? styles.hidePaymentInstrumentsTable : styles['instrument-table']}>
        <DataTable
            id="instrumentTable"
            data={data}
            showSearch={false}
            selectedRows={selectedRows}
            onSelectionChange={handleSelection}
            selectionType="single"
        >
            <DisplayColumn
                key="accounts"
                id="accounts"
                textAlign="left"
                header={translator(messages.accounts)}
                sortable={false}
                cell={generateCellContent}
                cellClassName={styles.selectedPaymentCell}
            />
            <DisplayColumn
                id="actions"
                key="actions"
                textAlign="right"
                header={translator(messages.actions)}
                cellClassName={styles.selectedPaymentCell}
                sortable={false}
                cell={generateActionColumn}
            />
        </DataTable>
    </div>, [data, generateActionColumn, generateCellContent, handleSelection, selectedRows, translator])

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            addPaymentInstrument
        },
        resolveComponentMap: {
            selectPaymentMethodTable
        }
    };

    const overrides = {
        title: {
            content: getTitleContent(),
            visible: !shouldHidePaymentInstrumentsTable(paymentInstruments, paymentMethod)
        },
        noPaymentInstruments: {
            visible: shouldShowNoPaymentInstrumentText(paymentInstruments, paymentMethod),
            content: getNoPaymentInstrumentText(paymentMethod)
        },
        addPaymentButton: {
            visible: paymentMethod.value !== 'agencysweep'
        },
        paymentInstrumentAlert: {
            visible: showRestrictedPaymentMethodError,
            message: messages.restrictedPaymentMethodError
        },
        paymentInstrumentWarning: {
            visible: showRestrictedPaymentMethodWarning,
            message: messages.restrictedPaymentMethodWarning
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default MLSelectPaymentMethodComponent;